import React, { useEffect, useState } from 'react';
import { markEmployeeAttendance, viewEmployeeAttendance } from '../../http';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Loading from '../Loading';

const Attendance = () => {
  const { user } = useSelector(state => state.authSlice);
  const [isAttendanceMarked, setIsAttendanceMarked] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [resetData, setResetData] = useState([]);
  const [alreadyMarked, setAlreadyMarked] = useState(false);
  const [todayDate, setTodayDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  const years = [2020, 2021, 2022, 2023, 2024,2025,2026,2027,2028];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthDays = {
    "January": 31, "February": 28, "March": 31, "April": 30,
    "May": 31, "June": 30, "July": 31, "August": 31,
    "September": 30, "October": 31, "November": 30, "December": 31,
  };
  
  const numOfDays = monthDays[selectedMonth];
  const days = Array.from({ length: numOfDays }, (_, index) => index + 1);

  // Function to fetch attendance data
  const fetchAttendanceData = async () => {
    setLoading(true);
    const dt = new Date();
    const obj = {
      employeeID: user.id,
      year: dt.getFullYear(),
      month: dt.getMonth() + 1,
    };
    
    try {
      const res = await viewEmployeeAttendance(obj);
      
      if (res.data && Array.isArray(res.data.data)) {
        // Validate and sanitize attendance data
        const sanitizedData = res.data.data.map(item => {
          // If item is not an object or missing critical fields, construct a safe default
          if (!item || typeof item !== 'object') {
            return {
              date: 1,
              month: dt.getMonth() + 1,
              year: dt.getFullYear(),
              day: 'Unknown',
              present: false
            };
          }
          
          // Return item with defaults for missing fields
          return {
            date: item.date || 1,
            month: item.month || dt.getMonth() + 1,
            year: item.year || dt.getFullYear(),
            day: item.day || 'Unknown',
            present: !!item.present,
            ...item // Keep other properties
          };
        });
        
        setResetData(sanitizedData);
        setAttendance(sanitizedData);
        
        // Check if attendance is already marked today
        if (sanitizedData && sanitizedData.length > 0) {
          const today = new Date();
          const todayFormatted = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
          
          // Find today's entry if it exists
          const todayEntry = sanitizedData.find(entry => {
            const entryDate = `${entry.date}/${entry.month}/${entry.year}`;
            return entryDate === todayFormatted;
          });
          
          setIsAttendanceMarked(!!todayEntry);
          setTodayDate(todayEntry);
        }
      } else {
        // Handle empty or invalid data
        setResetData([]);
        setAttendance([]);
        setIsAttendanceMarked(false);
        toast.warning("No attendance data found or invalid data format");
      }
      
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch attendance data");
      setAttendance([]);
      setResetData([]);
      setLoading(false);
      console.error("Fetch attendance error:", error);
    }
  };

  useEffect(() => {
    fetchAttendanceData();
  }, [user.id]);

  const markAttendance = async () => {
    try {
      const res = await markEmployeeAttendance({ employeeID: user.id });
  
      // Check if response has expected data
      if (res && res.data) {
        const { success, message, newAttendance } = res.data;
  
        if (success) {
          toast.success(message);
          
          // Update the local attendance data with the new entry
          if (newAttendance && typeof newAttendance === 'object') {
            // Make sure the newAttendance has all required fields
            const safeAttendance = {
              date: newAttendance.date || new Date().getDate(),
              month: newAttendance.month || new Date().getMonth() + 1,
              year: newAttendance.year || new Date().getFullYear(),
              day: newAttendance.day || new Date().toLocaleDateString('en-US', { weekday: 'long' }),
              present: true,
              ...newAttendance
            };
            
            setAttendance(prevAttendance => {
              const updatedAttendance = [safeAttendance, ...prevAttendance];
              setResetData(updatedAttendance); // Update reset data too
              return updatedAttendance;
            });
          } else {
            // If no attendance object received, fetch attendance data again
            fetchAttendanceData();
          }
          
          setIsAttendanceMarked(true);
          
          // Set to first page to see the new attendance entry
          setCurrentPage(1);
        } else {
          toast.error(message || "Unable to mark attendance");
        }
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (error) {
      // Capture specific error message if available
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to mark attendance";
      toast.error(errorMessage);
      console.error("Error marking attendance:", error);
    }
  };
  
  const searchAttendance = async () => {
    if (!selectedYear && !selectedMonth && !selectedDay) return;
    setLoading(true);
    
    const obj = { employeeID: user.id };
    if (selectedYear) obj.year = selectedYear;
    if (selectedMonth) obj.month = months.findIndex(month => month === selectedMonth) + 1;
    if (selectedDay) obj.date = selectedDay;
    
    try {
      const res = await viewEmployeeAttendance(obj);
      
      if (res.data && Array.isArray(res.data.data)) {
        // Validate and sanitize search results
        const dt = new Date();
        const sanitizedData = res.data.data.map(item => {
          if (!item || typeof item !== 'object') {
            return {
              date: selectedDay || 1,
              month: months.findIndex(month => month === selectedMonth) + 1 || dt.getMonth() + 1,
              year: selectedYear || dt.getFullYear(),
              day: 'Unknown',
              present: false
            };
          }
          
          return {
            date: item.date || selectedDay || 1,
            month: item.month || (months.findIndex(month => month === selectedMonth) + 1) || dt.getMonth() + 1,
            year: item.year || selectedYear || dt.getFullYear(),
            day: item.day || 'Unknown',
            present: !!item.present,
            ...item
          };
        });
        
        setAttendance(sanitizedData);
      } else {
        setAttendance([]);
        toast.info("No attendance records found for the selected criteria");
      }
      
      setCurrentPage(1);
      setLoading(false);
    } catch (error) {
      toast.error("Error fetching attendance data");
      setAttendance([]);
      setLoading(false);
      console.error("Search attendance error:", error);
    }
  };

  const resetHandler = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setAttendance(resetData);
    setCurrentPage(1);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = attendance.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(attendance.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <>
      <div className="main-content">
        <section className="section">
          <div className="card">
            <div className="card-header d-flex justify-content-between">
              <h4>Attendance</h4>
              <button
                className={`btn btn-lg ${isAttendanceMarked ? "btn-success" : "btn-primary"} btn-icon-split`}
                onClick={markAttendance}
                disabled={isAttendanceMarked || loading}
              >
                {isAttendanceMarked ? "✓ Attendance Marked" : "Mark Attendance"}
              </button>
            </div>
          </div>
          
          <div className="d-flex justify-content-center w-100 mb-4">
            <div className="col">
              <select
                className='form-control select2'
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">Year</option>
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>        
                ))}
              </select>
            </div>
            <div className="col">
              <select
                className='form-control select2'
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">Month</option>
                {months.map(month => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div className="col">
              <select
                className='form-control select2'
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                disabled={!selectedMonth}
              >
                <option value="">Day</option>
                {days.map(day => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <button 
              onClick={searchAttendance} 
              className="btn btn-primary mr-5 col"
              disabled={loading}
            >
              Search
            </button>
            <button 
              onClick={resetHandler} 
              className="btn btn-danger col"
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </section>
        
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading attendance data...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive mt-3">
              <table className="table table-striped table-md center-text">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((att, idx) => (
                      <tr key={idx}>
                        <td>{indexOfFirstItem + idx + 1}</td>
                        <td>{att && att.date && att.month && att.year ? `${att.date}/${att.month}/${att.year}` : 'N/A'}</td>
                        <td>{att && att.day ? att.day : 'N/A'}</td>
                        <td>
                          <div className={`badge ${att && att.present ? "badge-success" : "badge-danger"}`}>
                            {att && att.present ? "Present" : "Absent"}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No attendance records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {attendance.length > 0 && (
              <nav className="pagination-controls d-flex justify-content-end mt-4">
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      onClick={handlePrev} 
                      className="page-link" 
                      disabled={currentPage === 1}>
                      Previous
                    </button>
                  </li>
                  {totalPages <= 5 ? (
                    [...Array(totalPages).keys()].map(number => (
                      <li key={number} className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}>
                        <button onClick={() => paginate(number + 1)} className="page-link">
                          {number + 1}
                        </button>
                      </li>
                    ))
                  ) : (
                    <>
                      {/* First page */}
                      <li className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
                        <button onClick={() => paginate(1)} className="page-link">1</button>
                      </li>
                      
                      {/* Ellipsis if needed */}
                      {currentPage > 3 && (
                        <li className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      )}
                      
                      {/* Pages around current */}
                      {[...Array(totalPages).keys()]
                        .filter(number => {
                          const page = number + 1;
                          return page > 1 && page < totalPages && 
                                Math.abs(currentPage - page) <= 1;
                        })
                        .map(number => (
                          <li key={number} className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}>
                            <button onClick={() => paginate(number + 1)} className="page-link">
                              {number + 1}
                            </button>
                          </li>
                        ))
                      }
                      
                      {/* Ellipsis if needed */}
                      {currentPage < totalPages - 2 && (
                        <li className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      )}
                      
                      {/* Last page */}
                      <li className={`page-item ${currentPage === totalPages ? 'active' : ''}`}>
                        <button onClick={() => paginate(totalPages)} className="page-link">{totalPages}</button>
                      </li>
                    </>
                  )}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      onClick={handleNext} 
                      className="page-link" 
                      disabled={currentPage === totalPages}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Attendance;
